import React from 'react'
import { useMediaQuery } from '@grupoboticario/flora-react-hooks'
import { Dict } from '@grupoboticario/flora-react-utils'
import { config, Tokens } from '@grupoboticario/flora'

const order = ['mobile', 'tablet', 'desktop', 'fullscreen', 'ultrawide']

const media = Object.keys(config.media)
  .filter((currentMedia) => order.includes(currentMedia))
  .sort((a, b) => order.indexOf(a) - order.indexOf(b))

const onlyMediaList = media.reduce((mediaDict, currentMedia) => {
  mediaDict[currentMedia] = (config.media as Dict)[currentMedia]

  if (currentMedia === 'mobile') {
    mediaDict[currentMedia] = config.media[currentMedia].replace(/(\d+)/, '0')
  }

  const currentBreakpoint = order.indexOf(currentMedia)
  const nextBreakpoint =
    currentBreakpoint > -1 && order[currentBreakpoint + 1] ? order[currentBreakpoint + 1] : -1

  const minWidth = mediaDict[currentMedia]
  const maxWidth = (config.media as Dict)[nextBreakpoint]
    ?.replace('min', 'max')
    ?.replace(/(\d+)/, (match: number) => `${Number(match) - 1}`)

  mediaDict[currentMedia] = minWidth + (nextBreakpoint ? ` and ${maxWidth}` : '')

  return mediaDict
}, {} as Dict)

const fromMediaList: Dict = media.reduce((mediaDict, currentMedia) => {
  mediaDict[currentMedia] = (config.media as Dict)[currentMedia]

  if (currentMedia === 'mobile') {
    mediaDict[currentMedia] = config.media[currentMedia].replace(/(\d+)/, '0')
  }

  return mediaDict
}, {} as Dict)

type Media = keyof typeof Tokens.media

export type HiddenProps = {
  only?: Media[]
  from?: Media
  children: React.ReactNode
}

type HiddenComponent = React.FunctionComponent<HiddenProps>

/**
 * @component
 * @name Hidden
 * @description Hidden component
 * @tutorial https://flora.gb.tech/docs/components/core/hidden
 */
export const Hidden = (({ only, from, children }) => {
  const onlyMatcher = !!only
    ?.map((breakpoint) => useMediaQuery(onlyMediaList[breakpoint]))
    .find((breakpoint) => breakpoint)

  const fromMatcher = from && !!useMediaQuery(fromMediaList[from])

  return <>{onlyMatcher || fromMatcher ? null : children}</>
}) as HiddenComponent
